import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import { toast } from 'react-toastify';
import ContentLayout from '../../../layout/ContentLayout';
import { Container } from '../../../components/content';
import ProtocolFormWizard from '../../../components/protocols/ProtocolFormWizard';
import protocolApi from '../../../api/protocol-api';
import { protocolToFormData, toDaysPayload } from '../../../utils/protocol-form.util';
import styles from '../../../styles/mobileInfoPage.module.scss';

export default function IzmeniProtokol() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [initialPlacement, setInitialPlacement] = useState({
    categories: [],
    rank: null,
    subcategories: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchProtocol = useCallback(async () => {
    setLoading(true);
    const { data, success, message } = await protocolApi.getById(id);
    if (success && data) {
      const loaded = protocolToFormData(data);
      setForm(loaded);
      setInitialPlacement({
        categories: loaded.categories,
        rank: loaded.rank,
        subcategories: loaded.subcategories,
      });
    } else {
      toast.error(message || 'Greška pri učitavanju protokola');
      navigate('/protokoli?tab=protokoli');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    fetchProtocol();
  }, [fetchProtocol]);

  const handleUpdate = async (formData) => {
    setSaving(true);

    // Categories, rank and subcategories are lineage-wide
    // placement, saved separately and without creating a version.
    // durationDays and weightRanges define the lineage's identity and can't
    // change here.
    const sameIds = (a, b) => [...(a || [])].sort().join() === [...(b || [])].sort().join();
    const subcategoriesChanged = !sameIds(initialPlacement.subcategories, formData.subcategories);
    const placementChanged =
      initialPlacement.rank !== (formData.rank ?? null) ||
      !sameIds(initialPlacement.categories, formData.categories) ||
      subcategoriesChanged;
    if (placementChanged) {
      // Tags are sent only when changed: the backend validates every tag it
      // receives, and an untouched one shouldn't block a rank/category edit.
      const placement = await protocolApi.updatePlacement(id, {
        categories: formData.categories,
        rank: formData.rank ?? null,
        ...(subcategoriesChanged && { subcategories: formData.subcategories || [] }),
      });
      if (!placement.success) {
        toast.error(placement.message || 'Greška pri čuvanju kategorija i redosleda');
        setSaving(false);
        return;
      }
    }

    const payload = {
      name: formData.name,
      imageUrl: formData.imageUrl || undefined,
      versionNotes: formData.versionNotes || undefined,
      days: toDaysPayload(formData.days),
    };

    const { success, message } = await protocolApi.createNewVersion(id, payload);
    if (success) {
      toast.success('Nova verzija protokola kreirana');
      navigate('/protokoli?tab=protokoli');
    } else {
      toast.error(message || 'Greška pri izmeni protokola');
    }
    setSaving(false);
  };

  if (loading || !form) {
    return (
      <Container>
        <ContentLayout>
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        </ContentLayout>
      </Container>
    );
  }

  return (
    <Container>
      <Link to="/protokoli?tab=protokoli" className="link_btn" style={{ marginBottom: '12px' }}>
        <img alt="back" src="/assets/icons/chevron.svg" />
        <span>Nazad</span>
      </Link>

      <ContentLayout pt={true}>
        <h2 className={styles.pageTitle} style={{ marginBottom: '24px' }}>
          Izmeni protokol
        </h2>

        <ProtocolFormWizard
          mode="edit"
          initialForm={form}
          saving={saving}
          onSubmit={handleUpdate}
        />
      </ContentLayout>
    </Container>
  );
}
